// Lib
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
// Api
import { useBulkTopUpCustomerBalanceMutation } from "rtkQuery/query/customersPaymentAPI";
import {
  useGetCustomersQuery,
  useLazyCreateCustomersReportQuery,
  useLazyGetCustomersQuery,
} from "rtkQuery/query/customersAPI";
import {
  useLazyGetUserQuery,
  useUpdateUserMutation,
} from "rtkQuery/query/usersAPI";
// Hooks
import {
  useDebounce,
  useExport,
  useNotification,
  usePermissions,
  useTable,
  useViewport,
} from "hooks";
// Types
import { TablePaginationConfig } from "antd/es/table";
import { FilterValue } from "antd/es/table/interface";
import { TableAction } from "types/common";
import { ETable } from "types/tableFilters";
import { UserResponseDto } from "types/users";
import { CustomerProfileResponseDto } from "types/customers";
// Theme
import { theme } from "theme";
// Constants
import { NOTIFICATIONS, rtkQueryParams } from "consts";
// Icons
import { DescriptionBlackIcon, PlusIcon, RightBurgerMenuIcon } from "icons";
// Helpers
import { getFiltersQuery } from "helpers/tableFiltersHelpers";
// Utils
import { errorHandler } from "utils/errorHandler";
// Components
import {
  DropDown,
  Export,
  //TODO: remove comment if BE is ready
  // Export,
  Pagination,
  Report,
  Table,
} from "components";
import { TopUpBalanceModal, UserModal, UserModalForm } from "components/Modals";
import { UsedCouponsModal } from "./components";
// Styled
import { FlexContainer, PageWrapper } from "styled/Box";
import { Typography } from "styled/Typography";
import { Button } from "styled/Buttons";

import { columns, filtersList } from "./config";

export const Customers: FC = () => {
  const navigate = useNavigate();
  const { openNotification } = useNotification();

  const { isDesktop } = useViewport();
  const { downloadCSV } = useExport();
  const { canUsersUpdate } = usePermissions();

  const {
    sortingOrder,
    sortBy,
    page,
    limit,
    //TODO: remove comment if BE is ready
    search,
    debouncedSearch,
    selectedFilters,
    setPage,
    setLimit,
    //TODO: remove comment if BE is ready
    // setSearch,
    handleSort,
    handleSetTableFilterValues,
  } = useTable({ name: ETable.Customers });

  const filtersQuery = getFiltersQuery(selectedFilters);

  const query = useDebounce<Record<string, string | string[]>>(
    filtersQuery,
    500,
  );

  const requestQuery = {
    ...(query && query),
    sortBy,
    sortingOrder,
    search: debouncedSearch,
  };

  const {
    data: customersData,
    isFetching: isLoading,
    error: customersError,
    refetch,
  } = useGetCustomersQuery(
    {
      query: { page, limit, ...requestQuery },
    },
    rtkQueryParams,
  );

  const [getExportCustomersData, { isFetching: isExportLoading }] =
    useLazyGetCustomersQuery();

  const [getUserData] = useLazyGetUserQuery();

  const [updateUser, { isLoading: isUserUpdateLoading }] =
    useUpdateUserMutation();

  const [topUpBalance, { isLoading: isTopUpBalanceLoading }] =
    useBulkTopUpCustomerBalanceMutation();

  const [createReport, { isLoading: isCreateReportLoading }] =
    useLazyCreateCustomersReportQuery();

  useEffect(() => {
    if (customersError) {
      errorHandler({ error: customersError, openNotification });
    }
  }, [customersError]);

  const [selected, setSelected] = useState<string[]>([]);
  const [topUpBalanceModal, setTopUpBalanceModal] = useState<boolean>(false);
  const [userModal, setUserModal] = useState<boolean | UserResponseDto>(false);
  const [usedCustomerCoupons, setUsedCustomerCoupons] = useState({
    id: "",
    name: "",
  });
  const [loadingId, setLoadingId] = useState<string | null>(null);

  const handleCreateReport = async () => {
    try {
      await createReport({
        query: {
          ...(filtersQuery && filtersQuery),
          sortBy,
          sortingOrder,
          search,
        },
      }).unwrap();

      openNotification({
        message: "Report created successfully",
      });
    } catch (error) {
      errorHandler({ error, openNotification });
    }
  };

  const onCounterClick = (
    e: React.MouseEvent<HTMLElement, MouseEvent>,
    count: number,
    id: string,
    name: string,
  ) => {
    e.preventDefault();
    e.stopPropagation();

    if (!count) {
      return;
    }
    setUsedCustomerCoupons({
      id,
      name,
    });
  };

  const tableColumns = useMemo(() => {
    return columns.map(col => {
      if (col.key === "couponUsageCount") {
        return {
          ...col,
          render: (count: number, record: CustomerProfileResponseDto) => (
            <Button.Notification
              type="link"
              onClick={e => {
                onCounterClick(e, count, record.id, record?.fullName as string);
              }}
            >
              {count}
            </Button.Notification>
          ),
        };
      }

      return col;
    });
  }, []);

  const cleanColumns = useMemo(() => {
    return tableColumns.map((col, i) => ({
      ...col,
      title: i === 0 ? `${selected.length} data selected` : col.title,
      sorter: false,
    }));
  }, [selected.length]);

  const handleTableChange = (
    _pagination: TablePaginationConfig,
    _filters: Record<string, FilterValue | null>,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    sorter: any,
  ) => {
    handleSort(sorter?.field, sorter?.order);
  };

  const onRow = (record: CustomerProfileResponseDto) => {
    return {
      onClick: () => {
        navigate(`/customers/${record.id}`);
      },
    };
  };

  const handleTopUpBalance = async (data: {
    fmcCentsAmount?: number;
    note?: string;
    purpose?: string;
  }) => {
    const { fmcCentsAmount, note, purpose } = data || {};

    const transactions = selected.map(id => {
      const transaction: any = {
        customerId: id,
        fmcCentsAmount,
        purpose,
      };

      if (note) {
        transaction.note = note;
      }

      return transaction;
    });

    try {
      await topUpBalance({ transactions }).unwrap();

      setTopUpBalanceModal(false);

      setSelected([]);

      openNotification({
        message: NOTIFICATIONS.BALANCE_UPDATED,
      });
    } catch (error) {
      errorHandler({ error, openNotification });
    }
  };

  const getExportData = async (): Promise<Record<string, unknown>[]> => {
    try {
      const data = await getExportCustomersData({
        query: {
          ...(query && query),
          limit: customersData?.totalCount,
          sortBy,
          sortingOrder,
          search: debouncedSearch,
        },
      }).unwrap();

      return data.items;
    } catch (error) {
      errorHandler({ error, openNotification });

      return [];
    }
  };

  const handleOpenUserModal = async (id: string) => {
    try {
      setLoadingId(id);
      const userData = await getUserData({ id }).unwrap();
      setUserModal(userData);
    } catch (error) {
      errorHandler({ error, openNotification });
    } finally {
      setLoadingId(null);
    }
  };

  const onUserSave = async (data: UserModalForm) => {
    const isEdit = !!userModal && typeof userModal !== "boolean";

    if (!isEdit) {
      return;
    }

    try {
      const {
        id,
        firstName,
        lastName,
        roleName,
        email,
        newPassword,
        phoneNumber,
        customerActivated,
      } = data || {};

      if (!id) return;

      await updateUser({
        id,
        firstName,
        lastName,
        roleName,
        email,
        phoneNumber,
        newPassword,
        customerActivated,
      }).unwrap();

      openNotification({ message: NOTIFICATIONS.CUSTOMER_UPDATE });

      refetch();

      setUserModal(false);
    } catch (error) {
      errorHandler({ error, openNotification });
    }
  };

  const rowSelection = {
    onChange: (_: React.Key[], selectedRows: any[]) => {
      setSelected(selectedRows.map(row => row.id));
    },
  };

  // TODO: add this action after client aprove
  const actions: TableAction[] = [
    // {
    //   title: "",
    //   Icon: PencilIcon,
    //   loadingId: loadingId,
    //   type: "Grey",
    //   disabled: !canUsersUpdate,
    //   onClick: (row: CustomerProfileResponseDto) => handleOpenUserModal(row.id),
    // },
  ];

  const DropDownMenuItems = [
    {
      key: "1",
      label: "Export",
      onClick: () =>
        downloadCSV({
          fileName: "customers",
          isDataLoading: false,
          columns: columns,
          getExportData: getExportData,
        }),
    },
    {
      key: "2",
      label: "Top up Balance",
      disabled: !selected.length,
      onClick: () => setTopUpBalanceModal(true),
    },
  ];

  return (
    <>
      <PageWrapper $fullwidth $column>
        <FlexContainer
          $fullwidth
          $column
          $grow={1}
          $padding={isDesktop && "0 0 32px"}
        >
          <FlexContainer
            $padding="0 0 24px"
            $align="center"
            $justify="space-between"
          >
            <Typography.H1>Customers</Typography.H1>

            {isDesktop ? (
              <FlexContainer $gap={8}>
                <Export
                  fileName="customers"
                  isLoading={isExportLoading}
                  isDataLoading={isLoading}
                  isDisabled={!customersData?.totalCount}
                  columns={columns}
                  getExportData={getExportData}
                />

                <Button.Heading
                  type="primary"
                  disabled={!selected.length}
                  icon={<PlusIcon fill={theme.color.white} />}
                  onClick={() => setTopUpBalanceModal(true)}
                >
                  Top up Balance
                </Button.Heading>
              </FlexContainer>
            ) : (
              <DropDown items={DropDownMenuItems} trigger={["click"]}>
                <Button.SquaredIcon icon={<RightBurgerMenuIcon />} />
              </DropDown>
            )}
          </FlexContainer>

          <Table
            isLoading={isLoading}
            withPagination={!isDesktop}
            rowKey={record => record.id}
            dataSource={customersData?.items || []}
            actions={actions}
            columns={!selected?.length ? tableColumns : cleanColumns}
            empty={{
              icon: DescriptionBlackIcon,
              title: "No customer to show.",
              description: "Try changing sections or change the filters",
            }}
            header={{
              //TODO: remove comment if BE is ready
              // search: {
              //   placeholder: "Search name or phone number",
              //   value: search,
              //   setValue: setSearch,
              // },
              totalCount: customersData?.totalCount,
              filter: {
                filters: filtersList,
                selected: selectedFilters,
                setValue: handleSetTableFilterValues,
              }, 
              suffix: (
                <Report
                  isLoading={isCreateReportLoading}
                  handleCreateReport={handleCreateReport}
                />
              ),
            }}
            //TODO: remove and add request if BE is finished
            rowSelection={{
              type: "checkbox",
              selectedRowKeys: selected,
              ...rowSelection,
            }}
            onChange={handleTableChange}
            onRow={onRow}
          />
        </FlexContainer>

        <Pagination
          onPage
          padding="12px 16px"
          limit={limit}
          page={page}
          setLimit={setLimit}
          setPage={setPage}
          showSizeChanger={!isDesktop}
          totalItems={customersData?.totalCount}
        />
      </PageWrapper>

      <UserModal
        customerModal
        userData={userModal}
        isLoading={isUserUpdateLoading}
        onSave={onUserSave}
        onClose={() => setUserModal(false)}
      />

      <TopUpBalanceModal
        onSave={handleTopUpBalance}
        open={topUpBalanceModal}
        isLoading={isTopUpBalanceLoading}
        onClose={() => setTopUpBalanceModal(false)}
      />

      <UsedCouponsModal
        usedCustomerCoupons={usedCustomerCoupons}
        setUsedCustomerCoupons={setUsedCustomerCoupons}
      />
    </>
  );
};
